import type {
  GetAdvertiserAccountDetailRequest,
  GetAdvertiserAccountDetailResponse,
  GetAdvertiserAccountsResponse,
  GetManagerAccountsResponse,
  QueryAccountHierarchiesRequest,
  QueryAccountLinksRequest,
  QueryAccountLinksResponse,
  QueryAccountsRequest,
  QueryAccountsResponse
} from '@flexible-reports/store/api/accounts/api-types';
import type {
  Account,
  AdvertiserAccount,
  AdvertiserAccountResponse,
  FullAccountHierarchy,
  ManagerAccount
} from '@flexible-reports/store/api/accounts/types';
import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery, getHeaders } from '@urc-ui/store/api/base';
import { HttpMethod, Tag } from '@urc-ui/store/api/types';
import { transformFromQueryAccountHierarchies } from '@flexible-reports/store/api/accounts/transformers/transform-from-query-account-hierarchies';
import { exponentialBackoffWithJitter } from '@flexible-reports/store/api/base';

type useGetManagerAccountsResponse = {
  managerAccounts: ManagerAccount[];
  managerAccountsMapById: Record<string, ManagerAccount>;
  warnings: string[] | null;
};

type useGetAdvertiserAccountsResponse = {
  advertiserAccounts: AdvertiserAccount[];
  advertisersMapById: Record<string, AdvertiserAccount>;
  warnings: string[] | null;
};

type useGetAdvertiserAccountDetailResponse = {
  advertisersMapById: Record<string, AdvertiserAccount>;
};

type QueryAccountLinksTransformedResponse = {
  accounts: Account[];
  accountsMapById: Record<string, Account>;
};

type QueryAccountsTransformedResponse = {
  accounts: Account[];
  accountsMapById: Record<string, Account>;
};

const transformAdvertiserAccount = ({
  accountId: advertiserAccountId,
  accountName: advertiserAccountName,
  ...rest
}: AdvertiserAccountResponse): AdvertiserAccount => {
  return {
    advertiserAccountId,
    advertiserAccountName,
    ...rest
  };
};

const transformManagerAccountsToMap = (managerAccounts: ManagerAccount[]): Record<string, ManagerAccount> => {
  return Object.fromEntries(managerAccounts.map(acc => [acc.managerAccountId, acc]));
};

const transformAdvertiserAccountsToMap = (advertiserAccounts: AdvertiserAccountResponse[]): Record<string, AdvertiserAccount> => {
  return Object.fromEntries(advertiserAccounts.map(acc => [acc.accountId, transformAdvertiserAccount(acc)]));
};

const transformAccountsToMap = (accounts: Account[]): Record<string, Account> => {
  return Object.fromEntries(accounts.map(acc => [acc.accountId, acc]));
};

export const accountsApi = createApi({
  reducerPath: 'accountsApi',
  baseQuery: retry(
    axiosBaseQuery({
      baseUrl: '/reporting/api'
    }),
    exponentialBackoffWithJitter({ baseDelay: 50, maxDelay: 5000, maxRetries: 5 })
  ),
  tagTypes: [Tag.ACCOUNTS],
  endpoints: builder => ({
    getManagerAccounts: builder.query<useGetManagerAccountsResponse, void>({
      query: () => {
        return {
          url: '/query/manager-accounts',
          method: HttpMethod.POST,
          headers: getHeaders(),
          data: { maxResults: null }
        };
      },
      transformResponse: (response: GetManagerAccountsResponse) => {
        return {
          managerAccounts: response.managerAccounts ?? [],
          managerAccountsMapById: transformManagerAccountsToMap(response.managerAccounts ?? []),
          warnings: response.warnings
        };
      },
      keepUnusedDataFor: 600
    }),
    getAdvertiserAccounts: builder.query<useGetAdvertiserAccountsResponse, void>({
      query: () => {
        return {
          url: '/query/advertiser-accounts',
          method: HttpMethod.POST,
          headers: getHeaders(),
          data: { maxResults: null }
        };
      },
      transformResponse: (response: GetAdvertiserAccountsResponse) => {
        return {
          advertiserAccounts: (response.advertiserAccounts ?? []).map(transformAdvertiserAccount),
          advertisersMapById: transformAdvertiserAccountsToMap(response.advertiserAccounts ?? []),
          warnings: response.warnings
        };
      },
      keepUnusedDataFor: 600
    }),
    getAdvertiserAccountDetail: builder.query<useGetAdvertiserAccountDetailResponse, GetAdvertiserAccountDetailRequest>({
      query: (request: GetAdvertiserAccountDetailRequest) => {
        return {
          url: '/retrieve/advertiser-accounts',
          method: HttpMethod.POST,
          data: request,
          headers: getHeaders()
        };
      },
      transformResponse: (response: GetAdvertiserAccountDetailResponse) => {
        return {
          advertisersMapById: transformAdvertiserAccountsToMap(response.advertiserAccounts ?? [])
        };
      },
      providesTags: [Tag.ACCOUNTS]
    }),
    queryAccountHierarchies: builder.query<FullAccountHierarchy, QueryAccountHierarchiesRequest>({
      query: (request: QueryAccountHierarchiesRequest) => {
        return {
          url: '/query/account-hierarchies',
          method: HttpMethod.POST,
          data: request,
          headers: getHeaders()
        };
      },
      transformResponse: transformFromQueryAccountHierarchies,
      keepUnusedDataFor: 600 // 10 minutes in seconds
    }),
    queryAccountLinks: builder.query<QueryAccountLinksTransformedResponse, QueryAccountLinksRequest>({
      query: (request: QueryAccountLinksRequest) => {
        return {
          url: '/query/account-links',
          method: HttpMethod.POST,
          data: request,
          headers: getHeaders()
        };
      },
      transformResponse: (response: QueryAccountLinksResponse) => {
        return {
          accounts: response.accounts ?? [],
          accountsMapById: transformAccountsToMap(response.accounts ?? [])
        };
      }
    }),
    queryAccounts: builder.query<QueryAccountsTransformedResponse, QueryAccountsRequest>({
      query: (request: QueryAccountsRequest) => {
        return {
          url: '/query/accounts',
          method: HttpMethod.POST,
          data: request,
          headers: getHeaders()
        };
      },
      transformResponse: (response: QueryAccountsResponse) => {
        return {
          accounts: response.accounts ?? [],
          accountsMapById: transformAccountsToMap(response.accounts ?? [])
        };
      },
      providesTags: [Tag.ACCOUNTS]
    })
  })
});

export const {
  useGetManagerAccountsQuery,
  useGetAdvertiserAccountsQuery,
  useGetAdvertiserAccountDetailQuery,
  useLazyQueryAccountHierarchiesQuery,
  useQueryAccountHierarchiesQuery,
  useQueryAccountLinksQuery,
  useQueryAccountsQuery
} = accountsApi;
